/**
 * The six Budgets and Contracts tabs — plan §11 "Tabs and UX" (R36), the
 * single source of truth for tab routing, nav labels and placeholder copy.
 * TabNav (nav links) and every placeholder screen (PlaceholderScreen) read
 * this list rather than each hard-coding its own title/description, so the
 * route path, the nav label and the on-screen copy can never drift apart.
 *
 * `description` is the plan §11 one-line description for that tab, kept
 * verbatim from the plan text (trimmed of internal section-number citations
 * like "(4.4)" or "(8.1)", which are provenance for this file's authors, not
 * copy for the screen). WP-03 builds every tab as a placeholder only — the
 * real screen for each lands in its own later-phase WP (see the comment atop
 * each tabs/<key>/*.tsx file).
 */
import type { LucideIcon } from 'lucide-react';
import { Inbox, FlaskConical, Table, LayoutGrid, FileSignature, Settings } from 'lucide-react';

export interface ContractsTabConfig {
  key: 'intake' | 'studies' | 'rateCard' | 'workspace' | 'contracts' | 'admin';
  /** Path segment under /contracts/ (e.g. 'rate-card' -> /contracts/rate-card). */
  path: string;
  /** Short label for the tab nav. */
  navLabel: string;
  /** Full title for the placeholder screen's PageHeader. */
  title: string;
  /** One-line description, verbatim from plan §11. */
  description: string;
  icon: LucideIcon;
}

export const CONTRACTS_TABS: ContractsTabConfig[] = [
  {
    key: 'intake',
    path: 'intake',
    navLabel: 'Intake',
    title: 'Intake',
    description:
      'Drive watch list (new, approved, ignored, ingested, failed), upload of an email attachment, ' +
      'assignment to study and negotiation with direction, round and date, approve and ignore, ' +
      'unreadable items with reasons.',
    icon: Inbox,
  },
  {
    key: 'studies',
    path: 'studies',
    navLabel: 'Studies',
    title: 'Studies',
    description:
      'Studies and their negotiations, tags (sponsor, CRO, therapeutic area, indication), ' +
      'recruitment difficulty, turnaround dates, status, the outcome note.',
    icon: FlaskConical,
  },
  {
    key: 'rateCard',
    path: 'rate-card',
    navLabel: 'Rate Card',
    title: 'Rate Card',
    description: 'The two views — Card and Outcomes — sit together because both answer what WCR actually got.',
    icon: Table,
  },
  {
    key: 'workspace',
    path: 'workspace',
    navLabel: 'Workspace',
    title: 'Workspace',
    description:
      'The grid with a right-hand panel of three tabs, Changes (proposed and accepted), Notes, and a slot ' +
      'reserved for the assistant; toolbar actions Apply rate card, Apply previous study, Accept all, Export; ' +
      'lock indicator.',
    icon: LayoutGrid,
  },
  {
    key: 'contracts',
    // Path is intentionally the bare tab name from plan §11 item 5, distinct
    // from the module's own /contracts mount point — see plan §11: "Kept
    // separate on purpose: Workspace and Contracts are both 'open a version
    // and work it' ... the user chose ... to keep them apart."
    path: 'contracts',
    navLabel: 'Contracts',
    title: 'Contracts',
    description:
      'Clause list with flags and similarity matches, position shown beside each flagged clause, apply clause ' +
      'language from a previous study, decisions, redline export.',
    icon: FileSignature,
  },
  {
    key: 'admin',
    path: 'admin',
    navLabel: 'Admin',
    title: 'Admin',
    description:
      'Master lists and merge tool, positions library, roles note (who may edit rate-card values), backtest status.',
    icon: Settings,
  },
];
